/* Zana takeover (design: ConsoleTakeover, ConsoleTakeoverConfirm/Done, and the
   takeover tour, ConsoleTour). She shows her plan, the owner approves, and she
   drives the console: the main column gets a violet frame, her cursor and a
   tag show what she is touching, a click anywhere or Esc pauses her, and every
   message waits on a card the owner can read, edit or skip. When she finishes
   (or is stopped) each change is listed with Undo for 10 minutes.

   The plans and their writes live in plans.ts; this file only performs them. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFleet } from '../data/FleetData';
import { PAGES, Svg, useToast } from '../kit';
import { availablePlans, Plan, PlanCtx, PlanStep, planMeta, practicePlan, Receipt } from './plans';
import { forgetJob, recallJob, rememberJob } from './done';
import { postJobReceipt, reportZanaFailure } from './failureLog';
import { reducedMotion, useBoxes, waitForEl, zSel } from './box';

export type TkPhase = 'offer' | 'plan' | 'run' | 'paused' | 'confirm' | 'error' | 'done' | 'stopped';

interface Kept { r: Receipt; at: number; state: 'kept' | 'undoing' | 'undone' }

export interface Tk {
  plan: Plan;
  /** The job the owner asked for; differs from `plan` only during the tour. */
  real: Plan;
  phase: TkPhase;
  i: number;
  /** Steps left out with Edit plan. */
  off: number[];
  doneSteps: number[];
  skipped: number[];
  receipts: Kept[];
  body: string;
  editing: boolean;
  editPlan: boolean;
  sending: boolean;
  error: string | null;
  startedAt: number;
  endedAt: number | null;
  practice: boolean;
  /** Shown instead of running, because this job already finished. */
  repeat: boolean;
  /** Tour lesson: 0 none, 1–5 the lessons, 6 "You're ready". */
  lesson: number;
  approved: boolean;
  pausedOnce: boolean;
}

interface TkApi {
  tk: Tk | null;
  start: (plan: Plan) => void;
  approve: () => void;
  cancel: () => void;
  toggleStep: (i: number) => void;
  setEditPlan: (on: boolean) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  send: () => void;
  skip: () => void;
  retry: () => void;
  setBody: (s: string) => void;
  setEditing: (on: boolean) => void;
  undo: (idx: number) => void;
  handBack: () => void;
  /** Clears the remembered result and shows the plan again. */
  again: () => void;
  /** Settings asks for the tour again. False when there is nothing to practise on. */
  replayTour: () => boolean;
  /* the tour */
  tourStart: () => void;
  tourSkip: () => void;
  tourNext: () => void;
  tourBack: () => void;
  ctx: PlanCtx;
}

const TkCtx = createContext<TkApi | null>(null);
export const useTakeover = () => useContext(TkCtx);

const TOUR_KEY = 'trasse.console.takeoverTour';
const tourSeen = () => { try { return localStorage.getItem(TOUR_KEY) === 'done'; } catch { return true; } };
const markTour = () => { try { localStorage.setItem(TOUR_KEY, 'done'); } catch { /* storage blocked */ } };
const UNDO_MS = 10 * 60 * 1000;
const DWELL_MS = 1900;
const STEP_MS = 20000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** A step that never answers must not leave the button on Sending. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(label)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}
/** What went wrong, in words: the raw Firestore message means nothing to an owner. */
const human = (e: unknown, fallback: string) => {
  const m = String((e as Error)?.message || '');
  if (/permission/i.test(m)) return 'Trasse wouldn’t let that change through. Check you are signed in as this school’s fleet admin, then try again.';
  if (/network|fetch|offline|unavailable/i.test(m)) return 'The connection dropped. Nothing was half-done — try again in a minute.';
  return m || fallback;
};
const fresh = (plan: Plan, phase: TkPhase): Tk => ({
  plan, real: plan, phase, i: 0, off: [], doneSteps: [], skipped: [], receipts: [], body: '', editing: false, editPlan: false,
  sending: false, error: null, startedAt: Date.now(), endedAt: null, practice: false, repeat: false, lesson: 0, approved: false, pausedOnce: false,
});

export const TakeoverProvider: React.FC<{ children: React.ReactNode; onOpen: () => void }> = ({ children, onOpen }) => {
  const f = useFleet();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const [tk, setTk] = useState<Tk | null>(null);
  const tkRef = useRef(tk); tkRef.current = tk;
  const locRef = useRef(loc); locRef.current = loc;
  const reportRef = useRef<(t: Tk, step: PlanStep | undefined, error: string) => void>(() => undefined);
  reportRef.current = (t, step, error) => {
    if (t.practice) return;
    void reportZanaFailure({
      orgCode: f.org.code,
      ask: t.real.ask,
      step: step?.t || 'A step',
      tag: step?.tag,
      page: step?.page,
      where: step?.where,
      phase: t.phase,
      error,
      path: locRef.current.pathname + locRef.current.search,
      ui: t.plan.steps.map((s) => s.t).join(' | '),
    });
  };

  const ctx = useMemo<PlanCtx>(() => ({
    f, by: { uid: currentUser?.uid ?? null, email: currentUser?.email ?? null }, signer: f.me.name.split(/\s+/)[0] || '',
  }), [f, currentUser?.uid, currentUser?.email]);

  const patch = useCallback((p: Partial<Tk> | ((t: Tk) => Partial<Tk>)) => setTk((t) => (t ? { ...t, ...(typeof p === 'function' ? p(t) : p) } : t)), []);

  /** The next step to run after `i`, past the ones left out. */
  const nextIdx = (t: Tk, from: number) => { let n = from; while (n < t.plan.steps.length && t.off.includes(n)) n++; return n; };

  const finish = useCallback((t: Tk): Partial<Tk> => {
    if (!t.practice) {
      const summary = t.receipts.map((k) => k.r.text).filter(Boolean).join(' · ') || 'Finished. Nothing was changed or sent.';
      rememberJob(f.org.code, currentUser?.uid || '', { id: t.real.id, ask: t.real.ask, summary, at: Date.now(), ok: true });
      postJobReceipt(f.org.code, `${t.real.ask} ${summary}`);
      const changes = t.receipts.length;
      toast.toast({ kind: 'zana', text: changes ? `Zana finished: ${changes === 1 ? '1 thing' : `${changes} things`} done` : 'Zana finished', actLabel: 'Review', onAct: onOpen, ms: 6000 });
    }
    return { phase: 'done', endedAt: Date.now() };
  }, [toast, onOpen, f.org.code, currentUser?.uid]);

  /* Receipts stay on this card. Zana's fleet memory takes receipts from one
     place only (useZanaTakeover, guarded by its test); bus changes made here
     are logged in busStatusEvents with source 'zana', which Alerts reads. */
  const keep = useCallback((t: Tk, rs: Receipt[]): Kept[] => [...t.receipts, ...rs.map((r) => ({ r, at: Date.now(), state: 'kept' as const }))], []);

  /* ── the runner: one step per (phase run, i) ── */
  const phase = tk?.phase; const idx = tk?.i; const planId = tk?.plan.id; const lesson = tk?.lesson;
  useEffect(() => {
    const t = tkRef.current;
    if (!t || t.phase !== 'run') return undefined;
    const i = nextIdx(t, t.i);
    if (i !== t.i) { patch({ i }); return undefined; }
    if (i >= t.plan.steps.length) { setTk((c) => (c ? { ...c, ...finish(c) } : c)); return undefined; }
    const step = t.plan.steps[i];
    // In the tour she waits before a message until the lesson about messages.
    if (t.practice && step.send && (t.lesson || 0) < 5) return undefined;
    let cancelled = false;
    (async () => {
      const want = PAGES[step.page] + (step.search || '');
      const here = locRef.current.pathname + locRef.current.search;
      if (here !== want && !(step.search == null && locRef.current.pathname === PAGES[step.page])) navigate(want);
      const el = await waitForEl(zSel(step.target), 4000, () => cancelled);
      if (cancelled) return;
      if (!el) console.warn('[takeover] nothing on the page carries data-z=', step.target);
      el?.scrollIntoView?.({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      await sleep(DWELL_MS);
      if (cancelled) return;
      if (step.send) { setTk((c) => (c && c.phase === 'run' && c.i === i ? { ...c, phase: 'confirm', body: step.send?.body || '', editing: false } : c)); return; }
      let rs: Receipt[] = [];
      if (step.run) {
        try {
          rs = await withTimeout(step.run(), STEP_MS, 'That step did not finish, so I stopped it. Nothing after it has run.');
        } catch (e) {
          const msg = human(e, 'That didn’t work.');
          reportRef.current(t, step, msg);
          setTk((c) => (c && c.i === i ? { ...c, phase: 'error', error: msg } : c));
          return;
        }
      }
      // A write that finished counts even if she was paused or stopped meanwhile.
      setTk((c) => {
        if (!c || c.i !== i) return c;
        const n = { ...c, receipts: keep(c, rs), doneSteps: [...c.doneSteps, i], i: i + 1 };
        if (c.phase === 'run' && nextIdx(n, i + 1) >= n.plan.steps.length) return { ...n, ...finish(n) };
        return n;
      });
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx, planId, lesson]);

  /* ── controls ── */
  const start = useCallback((plan: Plan) => {
    onOpen();
    const prior = recallJob(f.org.code, currentUser?.uid || '', plan.id);
    if (prior) {
      setTk({
        ...fresh(plan, 'done'),
        repeat: true,
        i: plan.steps.length,
        doneSteps: plan.steps.map((_, n) => n),
        receipts: [{ r: { text: prior.summary }, at: prior.at, state: 'kept' }],
        startedAt: prior.at,
        endedAt: Date.now(),
      });
      return;
    }
    /* The offer is shown once, the first time she is asked to take something
       over. Remembering it here means a refresh, a second "Zana can do it",
       or Why? cannot bring it back. Settings can still ask for it. */
    const offer = !tourSeen();
    if (offer) markTour();
    setTk(fresh(plan, offer ? 'offer' : 'plan'));
  }, [onOpen, f.org.code, currentUser?.uid]);
  const replayTour = useCallback(() => {
    const plan = tkRef.current?.real || availablePlans(ctx)[0] || null;
    if (!plan) return false;
    onOpen();
    setTk(fresh(plan, 'offer'));
    return true;
  }, [ctx, onOpen]);
  const approve = useCallback(() => setTk((t) => {
    if (!t) return t;
    if (t.practice && t.lesson === 2) setTimeout(() => patch({ lesson: 3 }), 500);
    return { ...t, phase: 'run', i: 0, editPlan: false, approved: true, startedAt: Date.now() };
  }), [patch]);
  const cancel = useCallback(() => setTk(null), []);
  const toggleStep = useCallback((i: number) => patch((t) => {
    const off = t.off.includes(i) ? t.off.filter((x) => x !== i) : [...t.off, i];
    return off.length >= t.plan.steps.length ? {} : { off };
  }), [patch]);
  const setEditPlan = useCallback((on: boolean) => patch({ editPlan: on }), [patch]);
  const pause = useCallback(() => patch((t) => (t.phase === 'run' ? { phase: 'paused', pausedOnce: true } : {})), [patch]);
  const resume = useCallback(() => patch((t) => (t.phase === 'paused' ? { phase: 'run' } : {})), [patch]);
  const undoAll = useCallback((rs: Kept[]) => {
    rs.forEach((k) => { if (k.r.undo && k.state === 'kept') void k.r.undo().catch(() => undefined); });
    patch((t) => ({ receipts: t.receipts.map((k) => (k.r.undo && k.state === 'kept' ? { ...k, state: 'undone' } : k)) }));
  }, [patch]);
  const stop = useCallback(() => setTk((t) => {
    if (!t) return t;
    if (t.practice) { markTour(); return { ...fresh(t.real, 'plan') }; }
    const undoable = t.receipts.filter((k) => k.r.undo && k.state === 'kept');
    toast.toast({ kind: 'info', text: 'Zana stopped. Finished steps were kept.', ms: 6000, ...(undoable.length ? { actLabel: 'Undo all', onAct: () => undoAll(undoable) } : {}) });
    return { ...t, phase: 'stopped', endedAt: Date.now() };
  }), [toast, undoAll]);
  const send = useCallback(() => {
    const t = tkRef.current;
    if (!t || t.phase !== 'confirm' || t.sending) return;
    const step = t.plan.steps[t.i];
    if (!step?.send) return;
    const body = t.body.trim() || step.send.body;
    patch({ sending: true });
    withTimeout(step.send.run(body), STEP_MS, 'That send did not finish, so I stopped it. Nothing after it has run.').then((rs) => setTk((c) => {
      if (!c) return c;
      const n = { ...c, sending: false, editing: false, receipts: keep(c, rs), doneSteps: [...c.doneSteps, c.i], i: c.i + 1, phase: 'run' as TkPhase };
      if (c.practice) return { ...n, phase: 'done', lesson: 6, endedAt: Date.now() };
      return nextIdx(n, n.i) >= n.plan.steps.length ? { ...n, ...finish(n) } : n;
    })).catch((e) => {
      const msg = human(e, 'The message did not go out.');
      const cur = tkRef.current;
      if (cur) reportRef.current(cur, step, msg);
      patch({ sending: false, phase: 'error', error: msg });
    });
  }, [patch, keep, finish]);
  const skip = useCallback(() => setTk((t) => {
    if (!t) return t;
    const n = { ...t, skipped: [...t.skipped, t.i], i: t.i + 1, phase: 'run' as TkPhase, error: null, editing: false };
    if (t.practice) return { ...n, phase: 'done', lesson: 6, endedAt: Date.now() };
    return nextIdx(n, n.i) >= n.plan.steps.length ? { ...n, ...finish(n) } : n;
  }), [finish]);
  const retry = useCallback(() => patch((t) => (t.plan.steps[t.i]?.send ? { phase: 'confirm', error: null } : { phase: 'run', error: null })), [patch]);
  const setBody = useCallback((s: string) => patch({ body: s }), [patch]);
  const setEditing = useCallback((on: boolean) => patch({ editing: on }), [patch]);
  const undo = useCallback((k: number) => {
    const t = tkRef.current;
    const item = t?.receipts[k];
    if (!t || !item?.r.undo || item.state !== 'kept') return;
    if (Date.now() - item.at > UNDO_MS) { toast.toast({ kind: 'info', text: 'Undo has run out for this one. Change it on its page.' }); return; }
    const mark = (state: Kept['state']) => patch((c) => ({ receipts: c.receipts.map((x, j) => (j === k ? { ...x, state } : x)) }));
    mark('undoing');
    item.r.undo().then(() => { mark('undone'); toast.toast({ kind: 'info', text: `Undone: ${item.r.text}`, ms: 3000 }); })
      .catch(() => { mark('kept'); toast.toast({ kind: 'error', text: 'Couldn’t undo that. Try again, or change it on its page.' }); });
  }, [patch, toast]);
  const handBack = useCallback(() => {
    const t = tkRef.current;
    setTk(null);
    if (t && !t.practice) navigate(PAGES[t.plan.home.page] + (t.plan.home.search || ''));
  }, [navigate]);
  const again = useCallback(() => {
    const t = tkRef.current;
    if (!t) return;
    forgetJob(f.org.code, currentUser?.uid || '', t.real.id);
    setTk(fresh(t.real, tourSeen() ? 'plan' : 'offer'));
  }, [f.org.code, currentUser?.uid]);

  /* the tour */
  const tourStart = useCallback(() => setTk((t) => (t ? { ...fresh(practicePlan(t.real), 'plan'), real: t.real, practice: true, lesson: 1 } : t)), []);
  const tourSkip = useCallback(() => setTk((t) => {
    if (!t) return t;
    markTour();
    return fresh(t.real, 'plan');
  }), []);
  const tourNext = useCallback(() => setTk((t) => {
    if (!t || !t.practice) return t;
    const l = t.lesson;
    if (l === 2 && !t.approved) return t;
    if (l === 3 && !t.doneSteps.length) return t;
    if (l === 4 && !t.pausedOnce) return t;
    if (l === 4) {
      const s = t.plan.steps.findIndex((x) => !!x.send);
      if (s < 0) { markTour(); return { ...t, lesson: 6, phase: 'done', endedAt: Date.now() }; }
      return { ...t, lesson: 5, i: s, phase: 'confirm', body: t.plan.steps[s].send?.body || '' };
    }
    if (l === 5) { markTour(); return { ...t, lesson: 6, phase: 'done', endedAt: Date.now() }; }
    return { ...t, lesson: l + 1 };
  }), []);
  const tourBack = useCallback(() => patch((t) => (t.lesson > 1 && t.lesson < 4 ? { lesson: t.lesson - 1 } : {})), [patch]);

  /* Esc pauses her (and never closes the panel while she drives). */
  useEffect(() => {
    if (!tk || (tk.phase !== 'run' && tk.phase !== 'confirm')) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation(); e.preventDefault();
      if (tkRef.current?.phase === 'run') pause();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [tk, pause]);

  const api = useMemo<TkApi>(() => ({
    tk, start, approve, cancel, toggleStep, setEditPlan, pause, resume, stop, send, skip, retry, setBody, setEditing, undo, handBack, again, replayTour,
    tourStart, tourSkip, tourNext, tourBack, ctx,
  }), [tk, start, approve, cancel, toggleStep, setEditPlan, pause, resume, stop, send, skip, retry, setBody, setEditing, undo, handBack, again, replayTour, tourStart, tourSkip, tourNext, tourBack, ctx]);
  return <TkCtx.Provider value={api}>{children}</TkCtx.Provider>;
};

/* ── what the owner sees on the page ─────────────────────────────────────── */

const ZanaGlyph: React.FC<{ s?: number }> = ({ s = 20 }) => (
  <span aria-hidden="true" style={{ width: s, height: s, borderRadius: s * 0.3, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: s * 0.15, flex: 'none' }}>
    <i style={{ width: s * 0.15, height: s * 0.25, borderRadius: 3, background: '#fff', display: 'block' }} />
    <i style={{ width: s * 0.15, height: s * 0.25, borderRadius: 3, background: '#fff', display: 'block' }} />
  </span>
);

const inControl = (p: TkPhase) => p === 'run' || p === 'paused' || p === 'confirm' || p === 'error';

/** Over the main column: catcher, frame, pill, ring, tag and cursor. */
export const TakeoverLayer: React.FC = () => {
  const api = useTakeover();
  const layer = useRef<HTMLDivElement>(null);
  const tk = api?.tk;
  const on = !!tk && inControl(tk.phase);
  const step = tk && tk.i < tk.plan.steps.length ? tk.plan.steps[tk.i] : null;
  const [box] = useBoxes(layer, [step ? zSel(step.target) : ''], on && !!step);
  if (!api || !tk) return null;
  const lessonCard = tk.practice && tk.lesson >= 1 && tk.lesson <= 5 ? <Lesson /> : null;
  if (!on) return lessonCard ? <div className="tk-layer" ref={layer}>{lessonCard}</div> : null;
  const total = tk.plan.steps.length - tk.off.length;
  const n = tk.plan.steps.slice(0, tk.i + 1).filter((_, j) => !tk.off.includes(j)).length;
  const paused = tk.phase === 'paused';
  const pill = paused ? (tk.practice ? 'Paused · you have the wheel' : 'Paused · click Resume to carry on')
    : tk.phase === 'confirm' ? 'Zana is waiting for your OK'
      : tk.phase === 'error' ? 'Zana stopped on a step that didn’t work'
        : `Zana is working · step ${n} of ${total} · Esc to pause`;
  const cursorTag = tk.phase === 'confirm' ? 'waiting' : paused ? 'paused' : step?.cursor || 'reading';
  return (
    <div className="tk-layer" ref={layer}>
      {tk.phase !== 'paused' && <div className="tk-catch" onClick={api.pause} aria-hidden="true" data-testid="tk-catch" />}
      <div className={'tk-frame' + (paused ? ' paused' : '')} />
      {tk.practice && <span className="practice">Practice · nothing is saved</span>}
      {box && (
        <div className="tk-ring" style={{ left: box.left - 4, top: box.top - 4, width: box.width + 8, height: box.height + 8 }}>
          <span className="ztag">{step?.label}</span>
        </div>
      )}
      <div className="tk-cursor" aria-hidden="true" style={{ left: box ? box.left + Math.min(box.width * 0.5, 220) : '46%', top: box ? box.top + Math.min(box.height * 0.6, 40) : 220 }}>
        <svg width="22" height="24" viewBox="0 0 22 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.3))' }}><path d="M2 2l17 9-7.5 2L8 21z" fill="#6F4FF0" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" /></svg>
        <span>Zana · {cursorTag}</span>
      </div>
      <div className={'tk-pill' + (tk.practice && tk.lesson === 4 ? ' spot' : '')} role="status">
        <ZanaGlyph />
        <span>{pill}</span>
        {(tk.phase === 'run' || paused) && <button type="button" onClick={paused ? api.resume : api.pause}>{paused ? (tk.practice ? 'Carry on' : 'Resume') : 'Pause'}</button>}
        <button type="button" className="stop" onClick={api.stop}>Stop</button>
      </div>
      {lessonCard}
    </div>
  );
};

/* ── the tour's lesson card ── */
const LESSONS: [string, string][] = [
  ['Ask, or hand me a job', 'Ask a question, or say “do it for me”. I always make a plan before I touch anything.'],
  ['I show my plan first', 'Nothing changes until you approve. Each step says whether I’ll read, change or send. Press Approve to try it.'],
  ['You can see me working', 'The violet frame means I’m driving. My cursor and tags show exactly what I’m touching.'],
  ['You can stop me any time', 'Click anywhere on the page to pause me. Try it now.'],
  ['I always ask before I send', 'Messages wait for your OK, and you can edit them first. You can undo my changes for 10 minutes.'],
];
const Lesson: React.FC = () => {
  const api = useTakeover();
  const tk = api?.tk;
  if (!api || !tk) return null;
  const t = tk.lesson;
  const waitFor = t === 2 && !tk.approved ? 'Press Approve in the panel' : t === 3 && !tk.doneSteps.length ? 'Watch me work…' : t === 4 && !tk.pausedOnce ? 'Click the page to pause me' : '';
  const pos: React.CSSProperties = t === 4 ? { left: '50%', bottom: 90, transform: 'translateX(-50%)' } : t === 1 ? { right: 24, bottom: 24 } : { right: 24, top: 96 };
  return (
    <div className="lesson" role="dialog" aria-label="Takeover tour" style={{ ...pos, pointerEvents: 'auto' }}>
      <span className="eb">Takeover tour · {t} of 5</span>
      <h3>{LESSONS[t - 1][0]}</h3>
      <p>{LESSONS[t - 1][1]}</p>
      {waitFor && <span className="chip c-z" style={{ alignSelf: 'flex-start' }}>{waitFor}</span>}
      <div className="row" style={{ gap: 8, marginTop: 6 }}>
        <div className="dots" aria-hidden="true">{[1, 2, 3, 4, 5].map((i) => <i key={i} className={i === t ? 'on' : i < t ? 'done' : ''} />)}</div>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={api.tourSkip}>Skip tour</button>
        {t > 1 && t < 4 && <button type="button" className="btn s b-line" onClick={api.tourBack}>Back</button>}
        <button type="button" className="btn s" disabled={!!waitFor} onClick={api.tourNext}
          style={waitFor ? { background: 'var(--s2)', color: 'var(--faint)', cursor: 'default' } : { background: 'var(--z)', color: '#fff' }}>{t === 5 ? 'Finish' : 'Next'}</button>
      </div>
    </div>
  );
};

/* ── the panel while she has the job ─────────────────────────────────────── */

const TAGC: Record<string, string> = { Read: 'c-mute', Change: 'c-teal', Send: 'c-warn' };
const Check = () => <Svg size={16}><path d="m5 12 5 5 9-10" /></Svg>;

export function takeoverHead(tk: Tk): string {
  const pr = tk.practice ? ' · practice' : '';
  if (tk.phase === 'run' || tk.phase === 'paused') return 'Working on the page' + pr;
  if (tk.phase === 'confirm') return 'Waiting for you' + pr;
  if (tk.phase === 'error') return 'Stopped on a step';
  return 'Your fleet assistant';
}

export const TakeoverBody: React.FC = () => {
  const api = useTakeover();
  const tk = api?.tk;
  const offers = useMemo(() => (api && tk?.lesson === 6 ? availablePlans(api.ctx).filter((p) => p.id !== tk.real.id).slice(0, 2) : []), [api, tk?.lesson, tk?.real.id]);
  if (!api || !tk) return null;
  const { plan, phase } = tk;
  const total = plan.steps.length - tk.off.length;
  const n = plan.steps.slice(0, tk.i + 1).filter((_, j) => !tk.off.includes(j)).length;
  const running = phase === 'run' || phase === 'paused';
  const step = plan.steps[tk.i];
  const title = phase === 'plan' ? 'Here’s my plan' : phase === 'paused' ? `Paused at step ${n}` : phase === 'confirm' ? `Step ${n} of ${total} · waiting for you`
    : phase === 'error' ? `Step ${n} didn’t work` : phase === 'stopped' ? 'Stopped' : phase === 'done' ? 'Finished' : `Step ${Math.min(n, total)} of ${total}`;
  const changes = tk.receipts.length;
  const meta = phase === 'plan' ? planMeta({ ...plan, steps: plan.steps.filter((_, j) => !tk.off.includes(j)) })
    : running ? (step?.doing || 'Working…') : phase === 'confirm' ? 'Nothing is sent until you say so.'
      : phase === 'error' ? 'Nothing after this step has run.' : `${changes === 1 ? '1 thing' : `${changes} things`} done`;
  const spotPlan = tk.practice && tk.lesson === 2 ? ' spot' : '';
  const spotConfirm = tk.practice && tk.lesson === 5 ? ' spot' : '';
  const secs = tk.endedAt ? Math.max(1, Math.round((tk.endedAt - tk.startedAt) / 1000)) : 0;

  return (
    <>
      <div className="zq">{tk.real.ask}</div>

      {phase === 'offer' && (
        <div className="zcard zin-a">
          <b style={{ fontSize: 16 }}>Before I take over, want a 40-second tour?</b>
          <span className="sm" style={{ color: 'var(--muted)' }}>You’ll practise on this page. Nothing is saved.</span>
          <div className="zacts">
            <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff', position: 'relative', zIndex: 2 }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); api.tourStart(); }}>Show me</button>
            <button type="button" className="btn s b-line" style={{ position: 'relative', zIndex: 2 }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); api.tourSkip(); }}>Not now</button>
          </div>
        </div>
      )}

      {phase !== 'offer' && tk.lesson !== 6 && (
        <div className={'zcard zin-a' + spotPlan} style={{ gap: 6 }}>
          <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
            <b style={{ fontSize: 16 }}>{title}</b>
            <span className="chip c-z"><ZanaGlyph s={14} /> Takeover</span>
          </div>
          <span className="sm" style={{ color: 'var(--muted)' }}>{meta}</span>
          <div style={{ marginTop: 6 }}>
            {plan.steps.map((s, j) => {
              const off = tk.off.includes(j);
              const done = tk.doneSteps.includes(j);
              const skipped = tk.skipped.includes(j);
              const cur = !done && j === tk.i && (running || phase === 'confirm' || phase === 'error');
              if (off && !tk.editPlan) return null;
              return (
                <div key={j} className="tk-step" style={off ? { opacity: 0.5 } : undefined}>
                  {tk.editPlan ? (
                    <input type="checkbox" checked={!off} onChange={() => api.toggleStep(j)} aria-label={`Include: ${s.t}`} style={{ width: 18, height: 18, margin: 2, accentColor: 'var(--z)' }} />
                  ) : (
                    <span className={'tk-dot' + (done ? ' done' : skipped ? ' skip' : cur ? ' cur' : '')}>{done ? '✓' : skipped ? '–' : j + 1 - tk.off.filter((x) => x < j).length}</span>
                  )}
                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 14, lineHeight: '20px', ...(done || skipped ? { color: 'var(--muted)' } : cur ? { fontWeight: 600 } : {}), ...(skipped ? { textDecoration: 'line-through' } : {}) }}>{s.t}</span>
                    <span className="sm" style={{ color: 'var(--muted)' }}>{s.where}{skipped ? ' · skipped' : ''}</span>
                  </span>
                  <span className={'chip ' + TAGC[s.tag]} style={{ height: 22, fontSize: 12 }}>{s.tag}</span>
                </div>
              );
            })}
          </div>
          {phase === 'plan' && (
            <>
              <div className="zacts" style={{ marginTop: 8 }}>
                <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff' }} onClick={api.approve}>{tk.practice ? 'Approve' : 'Approve and run'}</button>
                <button type="button" className="btn s b-line" onClick={() => api.setEditPlan(!tk.editPlan)} aria-pressed={tk.editPlan}>{tk.editPlan ? 'Done editing' : 'Edit plan'}</button>
                {!tk.practice && <button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={api.cancel}>Cancel</button>}
              </div>
              <p className="sm" style={{ margin: '6px 0 0', color: 'var(--faint)' }}>{tk.editPlan ? 'Untick a step to leave it out.' : 'I’ll stop and ask again before sending anything.'}</p>
            </>
          )}
          {running && (
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <button type="button" className="btn s b-line" onClick={phase === 'paused' ? api.resume : api.pause}>{phase === 'paused' ? 'Resume' : 'Pause'}</button>
              <button type="button" className="btn s b-line" onClick={api.stop} style={{ color: 'var(--bad)' }}>
                <Svg size={14}><rect x="6" y="6" width="12" height="12" rx="2" /></Svg> Stop
              </button>
              <span className="sm" style={{ color: 'var(--muted)', marginLeft: 'auto' }}>Esc pauses</span>
            </div>
          )}
        </div>
      )}

      {phase === 'confirm' && step?.send && (
        <div className={'zcard zin-a' + spotConfirm} style={{ borderColor: 'var(--z-line)', boxShadow: '0 0 0 3px var(--z-tint)' }}>
          <div className="row" style={{ gap: 8 }}>
            <Svg size={16}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></Svg>
            <b>Ready to send. Check it first.</b>
          </div>
          {step.send.diff && (
            <div className="tk-diff">
              <div><span>{step.send.diff.label}</span></div>
              <div>
                {step.send.diff.minus && <div style={{ color: 'var(--bad)', textDecoration: 'line-through' }}>− {step.send.diff.minus}</div>}
                <div style={{ color: 'var(--ok)' }}>+ {step.send.diff.plus}</div>
              </div>
            </div>
          )}
          <div className="tk-msg">
            <span className="sm" style={{ display: 'block', color: 'var(--muted)', fontWeight: 600, marginBottom: 4 }}>{step.send.to}</span>
            {tk.editing
              ? <textarea value={tk.body} onChange={(e) => api.setBody(e.target.value)} aria-label="Message" autoFocus />
              : <span style={{ whiteSpace: 'pre-line' }}>{tk.body}</span>}
          </div>
          <div className="zacts">
            <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff' }} onClick={api.send} disabled={tk.sending || !tk.body.trim()}>
              {tk.sending ? 'Sending…' : step.send.button}
            </button>
            <button type="button" className="btn s b-line" onClick={() => api.setEditing(!tk.editing)}>{tk.editing ? 'Done' : 'Edit message'}</button>
            <button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={api.skip} disabled={tk.sending}>Skip</button>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div className="zcard zin-a">
          <div className="row" style={{ gap: 8, color: 'var(--bad)' }}><Svg size={16}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.5" /></Svg><b style={{ color: 'var(--ink)' }}>{step?.t}</b></div>
          <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>{tk.error}</p>
          {!tk.practice && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>A note of this step, the page and the time is saved for the Trasse team.</p>}
          <div className="zacts">
            <button type="button" className="btn s b-ink" onClick={api.retry}>Try again</button>
            <button type="button" className="btn s b-line" onClick={api.skip}>Skip this step</button>
            <button type="button" className="btn s b-ghost" style={{ color: 'var(--bad)' }} onClick={api.stop}>Stop</button>
          </div>
        </div>
      )}

      {(phase === 'done' || phase === 'stopped') && tk.lesson !== 6 && (
        <div className="zcard zin-a">
          <div className="row" style={{ gap: 8 }}>
            <span style={{ color: phase === 'done' ? 'var(--ok)' : 'var(--muted)' }}><Check /></span>
            <b style={{ fontSize: 16 }}>{phase === 'stopped' ? 'Stopped. Steps already done are kept.' : tk.repeat ? 'Already done' : `Done in ${secs} second${secs === 1 ? '' : 's'}`}</b>
          </div>
          {tk.repeat && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>This is what happened last time.</p>}
          {tk.receipts.length === 0 && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>Nothing was changed or sent.</p>}
          {tk.receipts.map((k, j) => {
            const late = Date.now() - k.at > UNDO_MS;
            return (
              <div key={j} className="row" style={{ gap: 10, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
                <span style={{ flex: 1, fontSize: 14, lineHeight: '20px', ...(k.state === 'undone' ? { color: 'var(--faint)', textDecoration: 'line-through' } : {}) }}>
                  {k.state === 'undone' ? `Undone: ${k.r.text}` : k.r.text}
                </span>
                {k.r.undo && k.state !== 'undone' && !late && (
                  <button type="button" className="btn s b-ghost" onClick={() => api.undo(j)} disabled={k.state === 'undoing'} title={k.r.undoNote}>{k.state === 'undoing' ? 'Undoing…' : 'Undo'}</button>
                )}
                {!k.r.undo && <span className="sm" style={{ color: 'var(--faint)' }}>Sent</span>}
              </div>
            );
          })}
          {tk.receipts.some((k) => k.r.undo) && (
            <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>Undo works for 10 minutes. A message can’t be unsent. Bus changes show in Alerts, marked as Zana’s.</p>
          )}
          <div className="zacts">
            <button type="button" className="btn s b-ink" onClick={api.handBack}>Hand back</button>
            {tk.repeat && <button type="button" className="btn s b-line" onClick={api.again}>Do it again</button>}
          </div>
        </div>
      )}

      {tk.lesson === 6 && (
        <div className="zcard zin-a">
          <b style={{ fontSize: 16 }}>You’re ready.</b>
          <span className="sm" style={{ color: 'var(--muted)' }}>Try a real one when you like. I’ll still show you my plan first.</span>
          <div className="fchips" style={{ padding: 0 }}>
            {[tk.real, ...offers].map((p) => (
              <button key={p.id} type="button" onClick={() => api.start(p)} style={{ height: 'auto', minHeight: 28, padding: '5px 10px', lineHeight: '18px', textAlign: 'left' }}>{p.ask.replace(/\.$/, '')}</button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
